import MapboxDraw from '@mapbox/mapbox-gl-draw';
import simplify from '@turf/simplify';

const Constants = MapboxDraw.constants;
const { CommonSelectors, doubleClickZoom } = MapboxDraw.lib;

/**
 * Factory for the shared click-by-click / press-drag-release polygon draw mode.
 * @param {Object} [options]
 * @param {number} [options.maxVertices] - once this many vertices are committed, the polygon
 *   auto-closes instead of accepting further clicks/drag points. Omit for no cap.
 * @param {(count: number) => void} [options.onVertexCountChange] - called with the current
 *   committed vertex count after every vertex is added (click or drag), so callers can warn
 *   before the cap is hit.
 */
export function createFreehandMode(options = {}) {
  const { maxVertices, onVertexCountChange } = options;

  const reportCount = (count) => {
    if (typeof onVertexCountChange === 'function') onVertexCountChange(count);
  };

  return {
    onSetup() {
      const polygon = this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: {},
        geometry: {
          type: Constants.geojsonTypes.POLYGON,
          coordinates: [[]],
        },
      });

      this.addFeature(polygon);
      this.clearSelectedFeatures();

      if (this.map?.dragPan) {
        this.map.dragPan.disable();
      }
      if (doubleClickZoom) {
        doubleClickZoom.disable(this);
      }

      this.updateUIClasses({ mouse: Constants.cursors.ADD });
      this.activateUIButton(Constants.types.POLYGON);
      this.setActionableState({ trash: true });

      return {
        polygon,
        currentVertexPosition: 0,
        isFreehand: false,
        isMouseDown: false,
        startLngLat: null,
        lastLngLat: null,
        dragPointsCount: 0,
        committed: false,
      };
    },

    onMouseDown(state, e) {
      if (CommonSelectors.isVertex(e)) return;
      const oe = e.originalEvent;
      if (oe && 'button' in oe && typeof oe.button === 'number' && oe.button !== 0) return;

      state.isMouseDown = true;
      state.startLngLat = e.lngLat;
      state.lastLngLat = e.lngLat;
      state.dragPointsCount = 0;
    },

    onTouchStart(state, e) {
      this.onMouseDown(state, e);
    },

    onDrag(state, e) {
      if (!state.isMouseDown || !state.startLngLat) return;

      const currentLng = e.lngLat.lng;
      const currentLat = e.lngLat.lat;

      // Check if movement is large enough to be a continuous freehand drag.
      // Only eligible to start on the very first vertex — otherwise a stray drag inside
      // a click (e.g. the first press of a double-click meant to finish the polygon)
      // would wipe out every vertex already placed via click-by-click mode.
      if (!state.isFreehand && state.currentVertexPosition === 0) {
        const dLng = Math.abs(currentLng - state.startLngLat.lng);
        const dLat = Math.abs(currentLat - state.startLngLat.lat);
        if (dLng > 1e-4 || dLat > 1e-4) {
          state.isFreehand = true;
          state.polygon.setCoordinates([[
            [state.startLngLat.lng, state.startLngLat.lat],
            [currentLng, currentLat],
          ]]);
          state.dragPointsCount = 2;
          reportCount(2);
          return;
        }
      }

      if (state.isFreehand) {
        if (state.lastLngLat) {
          const dLng = Math.abs(currentLng - state.lastLngLat.lng);
          const dLat = Math.abs(currentLat - state.lastLngLat.lat);
          if (dLng < 1e-6 && dLat < 1e-6) return;
        }
        state.lastLngLat = e.lngLat;

        this.updateUIClasses({ mouse: Constants.cursors.ADD });
        state.polygon.updateCoordinate(`0.${state.dragPointsCount}`, currentLng, currentLat);
        state.dragPointsCount++;
        reportCount(state.dragPointsCount);

        if (maxVertices && state.dragPointsCount >= maxVertices) {
          // Cap reached mid-drag: close the polygon now instead of accepting more points.
          this.finishFreehand(state);
          return;
        }

        state.polygon.updateCoordinate(`0.${state.dragPointsCount}`, currentLng, currentLat);
      }
    },

    onMouseMove(state, e) {
      if (!state.isMouseDown) {
        // Guide rubber-band line in click-by-click mode
        if (state.currentVertexPosition > 0) {
          state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, e.lngLat.lng, e.lngLat.lat);
        }
        return;
      }
      this.onDrag(state, e);
    },

    onTouchMove(state, e) {
      this.onDrag(state, e);
    },

    // Shared by onMouseUp (freehand drag ends normally) and onDrag (cap reached mid-drag).
    finishFreehand(state) {
      if (this.map?.dragPan) {
        this.map.dragPan.enable();
      }

      const coords = state.polygon.coordinates[0];
      if (!coords || coords.length < 3) {
        this.deleteFeature([state.polygon.id], { silent: true });
        this.changeMode(Constants.modes.SIMPLE_SELECT);
        return;
      }

      // Ensure closed ring
      if (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1]) {
        state.polygon.updateCoordinate(`0.${coords.length}`, coords[0][0], coords[0][1]);
      }

      try {
        const zoom = typeof this.map?.getZoom === 'function' ? this.map.getZoom() : 12;
        const tolerance = 1 / Math.pow(1.05, 10 * zoom);
        simplify(state.polygon, { mutate: true, tolerance, highQuality: true });
      } catch (err) {
        console.warn('Simplification error, keeping original coordinates:', err);
      }

      const finalCoords = state.polygon.coordinates[0];
      if (!finalCoords || finalCoords.length < 4) {
        this.deleteFeature([state.polygon.id], { silent: true });
        this.changeMode(Constants.modes.SIMPLE_SELECT);
        return;
      }

      state.committed = true;
      this.fire(Constants.events.CREATE, {
        features: [state.polygon.toGeoJSON()],
      });
      this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
    },

    onMouseUp(state, e) {
      const wasFreehand = state.isFreehand;
      const dragCount = state.dragPointsCount;
      state.isMouseDown = false;
      state.isFreehand = false;
      state.startLngLat = null;

      if (state.committed) {
        // Already closed mid-drag by hitting maxVertices; nothing left to do.
        return;
      }

      if (wasFreehand && dragCount >= 3) {
        this.finishFreehand(state);
        return;
      }

      // Click in click-by-click mode
      this.handleClick(state, e);
    },

    onTouchEnd(state, e) {
      this.onMouseUp(state, e);
    },

    // Shared by handleClick (closing click / cap reached) and onKeyUp (Enter key).
    finishClickPolygon(state) {
      if (this.map?.dragPan) {
        this.map.dragPan.enable();
      }

      const coords = state.polygon.coordinates[0] || [];
      const finalCoords = coords.slice(0, state.currentVertexPosition);
      if (finalCoords.length < 3) return false;

      if (finalCoords[0][0] !== finalCoords[finalCoords.length - 1][0] ||
          finalCoords[0][1] !== finalCoords[finalCoords.length - 1][1]) {
        finalCoords.push([finalCoords[0][0], finalCoords[0][1]]);
      }
      state.polygon.setCoordinates([finalCoords]);

      state.committed = true;
      this.fire(Constants.events.CREATE, {
        features: [state.polygon.toGeoJSON()],
      });
      this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
      return true;
    },

    handleClick(state, e) {
      const coords = state.polygon.coordinates[0] || [];

      // Mirrors mapbox-gl-draw's own draw_polygon mode (isEventAtCoordinates): a click
      // landing at the exact same map coordinate as the previously placed vertex closes
      // the polygon. A genuine double-click naturally produces this — both clicks
      // unproject the same screen pixel to the identical lngLat — so this needs no
      // Date.now()-based timing heuristic (which was the fragile, misfiring part before).
      // Clicking on any already-placed vertex (CommonSelectors.isVertex) closes it too,
      // same as the official mode.
      const prevVertex = state.currentVertexPosition > 0 ? coords[state.currentVertexPosition - 1] : null;
      const isSameAsPrev = !!prevVertex && e.lngLat.lng === prevVertex[0] && e.lngLat.lat === prevVertex[1];
      const isOnExistingVertex = CommonSelectors.isVertex(e);

      if ((isSameAsPrev || isOnExistingVertex) && state.currentVertexPosition >= 3) {
        this.finishClickPolygon(state);
        return;
      }

      // Add new vertex point
      this.updateUIClasses({ mouse: Constants.cursors.ADD });
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, e.lngLat.lng, e.lngLat.lat);
      state.currentVertexPosition++;
      reportCount(state.currentVertexPosition);

      if (maxVertices && state.currentVertexPosition >= maxVertices) {
        // Cap reached: close the polygon now instead of accepting more clicks.
        this.finishClickPolygon(state);
        return;
      }

      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, e.lngLat.lng, e.lngLat.lat);
    },

    onKeyUp(state, e) {
      if (CommonSelectors.isEscapeKey(e)) {
        if (this.map?.dragPan) {
          this.map.dragPan.enable();
        }
        this.deleteFeature([state.polygon.id], { silent: true });
        this.changeMode(Constants.modes.SIMPLE_SELECT);
      } else if (CommonSelectors.isEnterKey(e)) {
        // Enter key completes polygon
        if (state.currentVertexPosition >= 3) {
          this.finishClickPolygon(state);
        }
      }
    },

    onTrash(state) {
      if (this.map?.dragPan) {
        this.map.dragPan.enable();
      }
      this.deleteFeature([state.polygon.id], { silent: true });
      this.changeMode(Constants.modes.SIMPLE_SELECT);
    },

    onStop(state) {
      this.updateUIClasses({ mouse: Constants.cursors.NONE });
      if (doubleClickZoom) {
        doubleClickZoom.enable(this);
      }
      this.activateUIButton();

      if (this.map?.dragPan) {
        this.map.dragPan.enable();
      }

      if (state?.polygon?.id && !state.committed && this.getFeature(state.polygon.id)) {
        this.deleteFeature([state.polygon.id], { silent: true });
      }
    },

    toDisplayFeatures(state, geojson, display) {
      const isExact = geojson.properties.id === state.polygon.id;
      geojson.properties.active = isExact ? Constants.activeStates.ACTIVE : Constants.activeStates.INACTIVE;
      if (!isExact) return display(geojson);

      if (!geojson.geometry?.coordinates || geojson.geometry.coordinates.length === 0 || geojson.geometry.coordinates[0].length === 0) return;

      const coordinateCount = geojson.geometry.coordinates[0].length;
      if (coordinateCount < 3) {
        display({
          type: Constants.geojsonTypes.FEATURE,
          properties: {
            meta: Constants.meta.FEATURE,
            active: Constants.activeStates.ACTIVE,
          },
          geometry: {
            type: Constants.geojsonTypes.LINE_STRING,
            coordinates: geojson.geometry.coordinates[0],
          },
        });
        geojson.geometry.coordinates[0].forEach((coord, index) => {
          display({
            type: Constants.geojsonTypes.FEATURE,
            properties: {
              meta: Constants.meta.VERTEX,
              parent: state.polygon.id,
              coord_path: `0.${index}`,
              active: (index === state.currentVertexPosition - 1) ? Constants.activeStates.ACTIVE : Constants.activeStates.INACTIVE,
            },
            geometry: {
              type: Constants.geojsonTypes.POINT,
              coordinates: coord,
            },
          });
        });
        return;
      }

      display(geojson);

      geojson.geometry.coordinates[0].forEach((coord, index) => {
        display({
          type: Constants.geojsonTypes.FEATURE,
          properties: {
            meta: Constants.meta.VERTEX,
            parent: state.polygon.id,
            coord_path: `0.${index}`,
            active: (index === 0 || index === state.currentVertexPosition - 1) ? Constants.activeStates.ACTIVE : Constants.activeStates.INACTIVE,
          },
          geometry: {
            type: Constants.geojsonTypes.POINT,
            coordinates: coord,
          },
        });
      });
    },
  };
}

const FreehandMode = createFreehandMode();
export default FreehandMode;
